import axios from "axios";
import store from "@/store";
import { message } from "ant-design-vue";
import { geoBlockedNotice, mfaCheckUnavailableNotice, retryAfterStepUp, withMfaHeader } from "@/utils/mfa";
import { requestStepUp } from "@/utils/stepUp";
import { reportGeoBlock } from "@/utils/geoBlockReport";
import { withClientHeaders } from "@/utils/clientHeaders";
import { handleStandardError } from "@/utils/standardErrorHandler";

/**
 * call-api(콜봇 내선 관리) 전용 axios 인스턴스.
 * 요청 문서: ~/md/front/admin-콜봇-내선관리-화면-요청.md "요청 계약 1"
 *
 * - 인증: CERTILIFE 토큰(`localStorage.accessTokenCerti`)을 Bearer 로 싣는다.
 * - `X-Acting-Company-Id` 는 싣지 않는다 — CERTILIFE 에게는 의미가 없고 call-api 도 무시한다.
 * - 응답: HTTP body(`response.data`)를 언랩해 돌려준다. 화면 코드가 studio 처럼
 *   `response.data` 가 바로 오는 것을 전제로 쓰였다.
 * - 전역 로딩 없음: 폴링·재조회가 10초마다 도는 인스턴스라 `store.commit("setIsLoading")`
 *   을 켜지 않는다. 화면 안 스켈레톤·버튼 로딩으로 대신한다. `showGlobalLoading` 옵션은
 *   받아도 무시한다.
 * - 오류: 봉투 `{ error: { code, message, details? } }` 를 가공하지 않고 reject 한다.
 *   에러 파서(views/Callbot/service/callApiError.js)가 `err.response.status`·`err.response.data`
 *   를 직접 읽는다. 401 만 counsel-axios 와 같은 토큰 갱신·큐·재시도를 태운다.
 */
const callApiInstance = axios.create({
  baseURL: process.env.VUE_APP_CALL_API_URL,
  timeout: 30000,
  headers: { "Content-Type": "application/json" },
});

callApiInstance.interceptors.request.use(
  (config) => {
    withClientHeaders(config);
    if (localStorage.getItem("accessTokenCerti")) {
      config.headers.Authorization = `Bearer ${localStorage.getItem("accessTokenCerti")}`;
      withMfaHeader(config);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isTokenRefreshing = false;
let failedRequestsQueue = [];

callApiInstance.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const originalRequest = error.config;

    // 국외 IP 의 모든 요청(403 GEO_BLOCKED): 안내 문구를 한 번(같은 key 라 겹치지 않음) 띄우고 그대로 실패시킨다(step-up 없음, AC-GEO-010c)
    const geoNotice = geoBlockedNotice(error);
    if (geoNotice) {
      reportGeoBlock("REQUEST", originalRequest);
      message.warning({ content: geoNotice, key: "GEO_BLOCKED" });
      return Promise.reject(error);
    }

    // 2FA 필요 여부 확인 실패(503 MFA_CHECK_UNAVAILABLE): 고정 문구 한 번(같은 key) 뒤 실패 — 재전송·step-up 없음(F-9)
    const unavailable = mfaCheckUnavailableNotice(error);
    if (unavailable) {
      message.error({ content: unavailable, key: "MFA_CHECK_UNAVAILABLE" });
      return Promise.reject(error);
    }

    // 2차 인증 필요(MFA_REQUIRED·SEND_AUTH_REQUIRED) → step-up 창 → 통과하면 원래 요청 한 번 재전송
    const stepUp = retryAfterStepUp(error, { requestStepUp, resend: callApiInstance });
    if (stepUp) return stepUp;

    // 새 계약 오류 코드: 문구는 콜봇 화면(callApiError)이 직접 보이므로 알림 없이, 갱신하지 않고 실패(W6 E2)
    if (await handleStandardError(error, { notify: false })) return Promise.reject(error);

    if (error.response && error.response.status === 401 && originalRequest && !originalRequest._retry) {
      if (!isTokenRefreshing) {
        isTokenRefreshing = true;
        await store.dispatch("requestAccessToken");
        isTokenRefreshing = false;

        // 큐에 쌓인 요청도 인스턴스를 거쳐 재호출되므로 응답 언랩이 그대로 적용된다.
        failedRequestsQueue.forEach((queueItem) => {
          queueItem.resolve(callApiInstance(queueItem.originalRequest));
        });
        failedRequestsQueue = [];

        originalRequest._retry = true;
        originalRequest.headers.Authorization = `Bearer ${localStorage.getItem("accessTokenCerti")}`;
        return callApiInstance(originalRequest);
      } else {
        return new Promise((resolve, reject) => {
          failedRequestsQueue.push({
            resolve,
            reject,
            originalRequest,
          });
        });
      }
    }
    return Promise.reject(error);
  }
);

export default callApiInstance;
